import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NuvemEnvioErroredLabelDto,
  NuvemEnvioReadyLabelDto,
} from '@tiendanube/common';
import { AsyncDocumentJobStatus } from '@tiendanube/common/src/enums';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  trackingNuvemEnvioPrintDocumentsGeneratingStartLabels,
  trackingNuvemEnvioPrintLabelsWaitingPayment,
} from '../../../tracking';
import ordersService from '../../ordersService/ordersService';
import {
  NE_POOLING_LABELS_SEARCH_PROCESS_INTERVAL,
  NE_POOLING_LABELS_SEARCH_PROCESS_TIMEOUT,
} from '../../utils';
import { combineLabelErrorCodes } from '../../utils/async-errors';
import {
  NE_POOLING_LABELS_INTERVAL,
  NE_POOLING_LABELS_TIMEOUT,
} from '../../utils/pooling';
import useCreateAsyncNuvemEnvioLabels from '../useCreateAsyncNuvemEnvioLabels/useCreateAsyncNuvemEnvioLabels';
import useDownloadNuvemEnvioLabel from '../useDownloadNuvemEnvioLabel/useDownloadNuvemEnvioLabel';
import useGetConcatenateLabels from '../useGetConcatenateLabels/useGetConcatenateLabels';
import { usePolling } from '../usePolling/usePolling';
import useSearchNuvemEnvioLabels from '../useSearchNuvemEnvioLabels/useSearchNuvemEnvioLabels';
import useSimulateNuvemEnvioLabels from '../useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';

export interface UseAsyncLabelPollingOrchestratorResult {
  isSimulationPolling: boolean;
  isSearchPolling: boolean;
  isConcatenationPolling: boolean;
  onProgress: boolean;
  hasError: boolean;
  hasSuccess: boolean;
  isSimulationTimeout: boolean;
  isTimeout: boolean;
  isConcatenationTimeout: boolean;

  progressInPercentage: number;
  totalOrders?: number;
  generatedOrders?: number;
  readyLabels?: NuvemEnvioReadyLabelDto[] | null;
  erroredLabels?: NuvemEnvioErroredLabelDto[] | null;
  concatenationDocumentUrl?: string;
  downloadDocumentUrl?: string;
  combinedErrorCodes: string;

  reset: () => void;
  initialize: (fulfillmentOrderIds: string[], orderId: string) => void;
}

interface UseAsyncLabelPollingOrchestratorParams {
  orderId: string;
}

function useAsyncLabelPollingOrchestrator({
  orderId,
}: UseAsyncLabelPollingOrchestratorParams): UseAsyncLabelPollingOrchestratorResult {
  const { id: storeId } = useStoreInfo();
  const {
    nuvemEnvioConfigs,
    isPostPaid,
    shouldUseBulkAsyncProcess,
    status: nuvemEnvioConfigsStatus,
  } = useGetNuvemEnvioConfigs();

  const {
    createNuvemEnvioAsyncLabels,
    resetNuvemEnvioAsyncLabels,
    data: createNuvemEnvioAsyncLabelsData,
    status: createNuvemEnvioAsyncLabelsStatus,
  } = useCreateAsyncNuvemEnvioLabels();

  const {
    searchNuvemEnvioLabels,
    resetSearchNuvemEnvioLabels,
    data: searchNuvemEnvioLabelsData,
    status: searchNuvemEnvioLabelsStatus,
  } = useSearchNuvemEnvioLabels();

  const {
    data: downloadNuvemEnvioLabelData,
    status: downloadNuvemEnvioLabelStatus,
    resetDownloadNuvemEnvioLabel,
  } = useDownloadNuvemEnvioLabel();

  const {
    data: simulateNuvemEnvioLabelsData,
    status: simulateNuvemEnvioLabelsStatus,
  } = useSimulateNuvemEnvioLabels();

  const concatenationJobId = useMemo(
    () =>
      (downloadNuvemEnvioLabelData as { concatenationJobId?: string })
        ?.concatenationJobId,
    [downloadNuvemEnvioLabelData],
  );

  const {
    getConcatenateLabels,
    resetConcatenateLabels,
    data: concatenateLabelsData,
    status: concatenateLabelsStatus,
  } = useGetConcatenateLabels(concatenationJobId || '');

  const [fulfillmentOrderIds, setFulfillmentOrderIds] = useState<string[]>([]);
  const [shouldStartSimulationPolling, setShouldStartSimulationPolling] =
    useState<boolean>(false);
  const [shouldStartSearchPolling, setShouldStartSearchPolling] =
    useState<boolean>(false);
  const [shouldStartConcatenationPolling, setShouldStartConcatenationPolling] =
    useState<boolean>(false);
  const [isSimulationTimeout, setIsSimulationTimeout] =
    useState<boolean>(false);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [isConcatenationTimeout, setIsConcatenationTimeout] =
    useState<boolean>(false);
  const [shouldCreateAsyncLabels, setShouldCreateAsyncLabels] =
    useState<boolean>(false);
  const [simulationStep, setSimulationStep] = useState<number>(0);
  const simulationTotalSteps = Math.max(
    1,
    Math.ceil(NE_POOLING_LABELS_TIMEOUT / NE_POOLING_LABELS_INTERVAL),
  );

  const initialize = (ids: string[], _orderId: string) => {
    if (!ids.length) return;

    if (
      (shouldStartSimulationPolling ||
        createNuvemEnvioAsyncLabelsStatus.isLoading) &&
      !shouldCreateAsyncLabels
    ) {
      return;
    }

    setFulfillmentOrderIds(ids);
    setIsTimeout(false);
    setIsSimulationTimeout(false);
    setShouldCreateAsyncLabels(false);

    const shouldStartPolling =
      nuvemEnvioConfigsStatus.isSuccess &&
      !isPostPaid &&
      (simulateNuvemEnvioLabelsStatus.isIdle ||
        !!simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge);

    if (shouldStartPolling) {
      setShouldStartSimulationPolling(true);
    } else if (nuvemEnvioConfigsStatus.isSuccess) {
      createNuvemEnvioAsyncLabels({
        fulfillmentOrderIds: ids,
        nuvemEnvioCarrierId: nuvemEnvioConfigs.nuvemEnvioCarrierId,
      });
    }
  };

  const reset = () => {
    resetNuvemEnvioAsyncLabels();
    resetSearchNuvemEnvioLabels();
    resetDownloadNuvemEnvioLabel();
    resetConcatenateLabels();
    setShouldStartSimulationPolling(false);
    setShouldStartSearchPolling(false);
    setShouldStartConcatenationPolling(false);
    setIsSimulationTimeout(false);
    setIsTimeout(false);
    setIsConcatenationTimeout(false);
    setShouldCreateAsyncLabels(false);
    setSimulationStep(0);
  };

  useEffect(() => {
    const canCreateLabels = isPostPaid ? true : shouldCreateAsyncLabels;

    if (
      canCreateLabels &&
      !isPostPaid &&
      fulfillmentOrderIds.length &&
      !shouldStartSimulationPolling
    ) {
      createNuvemEnvioAsyncLabels({
        fulfillmentOrderIds,
        nuvemEnvioCarrierId: nuvemEnvioConfigs.nuvemEnvioCarrierId,
      });
      setShouldCreateAsyncLabels(false);
    }
  }, [
    shouldCreateAsyncLabels,
    isPostPaid,
    fulfillmentOrderIds,
    nuvemEnvioConfigs.nuvemEnvioCarrierId,
    createNuvemEnvioAsyncLabels,
    shouldStartSimulationPolling,
    shouldUseBulkAsyncProcess,
  ]);

  useEffect(() => {
    if (
      createNuvemEnvioAsyncLabelsStatus.isSuccess &&
      createNuvemEnvioAsyncLabelsData?.labels?.length
    ) {
      setShouldStartSearchPolling(true);
    }
  }, [createNuvemEnvioAsyncLabelsStatus, createNuvemEnvioAsyncLabelsData]);

  useEffect(() => {
    if (
      searchNuvemEnvioLabelsStatus.isSuccess ||
      searchNuvemEnvioLabelsStatus.isError
    ) {
      setShouldStartSearchPolling(false);
    }
  }, [searchNuvemEnvioLabelsStatus]);

  useEffect(() => {
    if (concatenationJobId && downloadNuvemEnvioLabelStatus.isSuccess) {
      setShouldStartConcatenationPolling(true);
    }
  }, [concatenationJobId, downloadNuvemEnvioLabelStatus.isSuccess]);

  useEffect(() => {
    const status = concatenateLabelsData?.status;

    if (
      status === AsyncDocumentJobStatus.SUCCESS ||
      status === AsyncDocumentJobStatus.PARTIAL_SUCCESS ||
      status === AsyncDocumentJobStatus.FAILURE ||
      status === AsyncDocumentJobStatus.TIMEOUT ||
      concatenateLabelsStatus.isError
    ) {
      setShouldStartConcatenationPolling(false);
    }
  }, [concatenateLabelsData?.status, concatenateLabelsStatus.isError]);

  const handleSimulationSuccess = useCallback(() => {
    setShouldStartSimulationPolling(false);
    setShouldCreateAsyncLabels(true);
    setSimulationStep(simulationTotalSteps);
  }, [simulationTotalSteps]);

  const handleSimulationTimeout = useCallback(() => {
    trackingNuvemEnvioPrintLabelsWaitingPayment(storeId);
    setIsSimulationTimeout(true);
    setShouldStartSimulationPolling(false);
    setSimulationStep(simulationTotalSteps);
  }, [storeId, simulationTotalSteps]);

  const simulateLabelsCallback = useCallback(async () => {
    const response = await ordersService.simulateNuvemEnvioLabels({
      fulfillmentOrderIds,
    });
    setSimulationStep((prev) => {
      const next = Math.min(prev + 1, simulationTotalSteps);
      return next;
    });

    const shouldRecharge = response?.balanceInfo?.shouldRecharge;
    const success = !shouldRecharge;

    return success;
  }, [fulfillmentOrderIds, simulationTotalSteps]);

  usePolling(shouldStartSimulationPolling, simulateLabelsCallback, {
    interval: NE_POOLING_LABELS_INTERVAL,
    timeout: NE_POOLING_LABELS_TIMEOUT,
    onSuccess: handleSimulationSuccess,
    onTimeout: handleSimulationTimeout,
  });

  usePolling(
    shouldStartSearchPolling,
    async () => {
      trackingNuvemEnvioPrintDocumentsGeneratingStartLabels(orderId);
      await searchNuvemEnvioLabels({
        fulfillmentOrdersIds: createNuvemEnvioAsyncLabelsData.labels.map(
          (label) => label.fulfillmentOrderId,
        ),
      });

      return false;
    },
    {
      interval: NE_POOLING_LABELS_SEARCH_PROCESS_INTERVAL,
      timeout: NE_POOLING_LABELS_SEARCH_PROCESS_TIMEOUT,
      onTimeout: () => {
        setIsTimeout(true);
      },
    },
  );

  usePolling(
    shouldStartConcatenationPolling && !!concatenationJobId,
    async () => {
      if (!concatenationJobId) return true;

      const response = await getConcatenateLabels(concatenationJobId);

      const status = response?.status;

      if (
        status === AsyncDocumentJobStatus.SUCCESS ||
        status === AsyncDocumentJobStatus.PARTIAL_SUCCESS ||
        status === AsyncDocumentJobStatus.FAILURE ||
        status === AsyncDocumentJobStatus.TIMEOUT
      ) {
        return true;
      }

      return false;
    },
    {
      interval: NE_POOLING_LABELS_SEARCH_PROCESS_INTERVAL,
      timeout: NE_POOLING_LABELS_SEARCH_PROCESS_TIMEOUT,
      onTimeout: () => {
        setIsConcatenationTimeout(true);
      },
    },
  );

  const hasMultipleIds = fulfillmentOrderIds.length > 1;

  const baseError: boolean =
    createNuvemEnvioAsyncLabelsStatus.isError ||
    searchNuvemEnvioLabelsStatus.isError ||
    Boolean(createNuvemEnvioAsyncLabelsData?.errors?.length) ||
    Boolean(searchNuvemEnvioLabelsData?.erroredLabels?.length) ||
    isTimeout ||
    isSimulationTimeout;

  const concatenateError: boolean = hasMultipleIds
    ? concatenateLabelsStatus.isError ||
      (Boolean(concatenateLabelsData?.error?.code) &&
        concatenateLabelsData?.status !== 'PARTIAL_SUCCESS') ||
      isConcatenationTimeout
    : false;

  const hasError: boolean = baseError || concatenateError;

  const hasSuccess: boolean =
    searchNuvemEnvioLabelsStatus.isSuccess &&
    Boolean(searchNuvemEnvioLabelsData?.readyLabels?.length);

  const onProgress: boolean =
    !isTimeout &&
    !isSimulationTimeout &&
    (!hasMultipleIds || !isConcatenationTimeout) &&
    (shouldStartSimulationPolling ||
      createNuvemEnvioAsyncLabelsStatus.isLoading ||
      searchNuvemEnvioLabelsStatus.isLoading);

  const combinedErrorCodes = useMemo(
    () =>
      combineLabelErrorCodes(
        createNuvemEnvioAsyncLabelsData?.errors,
        searchNuvemEnvioLabelsData?.erroredLabels,
        isTimeout ||
          isSimulationTimeout ||
          (hasMultipleIds && isConcatenationTimeout),
      ),
    [
      createNuvemEnvioAsyncLabelsData?.errors,
      searchNuvemEnvioLabelsData?.erroredLabels,
      isTimeout,
      isSimulationTimeout,
      isConcatenationTimeout,
      hasMultipleIds,
    ],
  );

  const simulationProgressInPercentage = useMemo(
    () => Math.round((simulationStep / simulationTotalSteps) * 100),
    [simulationStep, simulationTotalSteps],
  );

  const progressInPercentage = useMemo(() => {
    if (shouldStartSimulationPolling) {
      return simulationProgressInPercentage;
    }

    if (isSimulationTimeout) {
      return 100;
    }

    return searchNuvemEnvioLabelsData?.progressInPercentage ?? 0;
  }, [
    shouldStartSimulationPolling,
    isSimulationTimeout,
    simulationProgressInPercentage,
    searchNuvemEnvioLabelsData?.progressInPercentage,
  ]);

  return {
    isSimulationPolling: shouldStartSimulationPolling,
    isSearchPolling: shouldStartSearchPolling,
    isConcatenationPolling: shouldStartConcatenationPolling,
    onProgress,
    hasError,
    hasSuccess,
    isSimulationTimeout,
    isTimeout,
    isConcatenationTimeout,
    progressInPercentage,
    totalOrders: searchNuvemEnvioLabelsData?.totalCount,
    generatedOrders: searchNuvemEnvioLabelsData?.finishedCount,
    readyLabels:
      (searchNuvemEnvioLabelsData?.readyLabels as
        | NuvemEnvioReadyLabelDto[]
        | null) || null,
    erroredLabels:
      (searchNuvemEnvioLabelsData?.erroredLabels as
        | NuvemEnvioErroredLabelDto[]
        | null) || null,
    concatenationDocumentUrl: concatenateLabelsData?.documentUrl,
    downloadDocumentUrl: downloadNuvemEnvioLabelData?.documentUrl,
    combinedErrorCodes,
    reset,
    initialize,
  };
}

export default useAsyncLabelPollingOrchestrator;
